import { useEffect, useState } from 'react'
import api from '../services/api'

// Complemento do funcionário (TMOTCA1 do ERP) — colunas da própria frota.cadmot.

type Aba = 'geral' | 'cnh' | 'banco' | 'beneficios' | 'pensao'
type Item = { cod: string | number; des: string }
interface Tabelas { classes: Item[]; bancos: Item[]; locais: Item[]; escalas: Item[]; domicilios: Item[]; jornadas: Item[] }

const ABAS: [Aba, string][] = [
  ['geral', 'Geral'], ['cnh', 'Inf. CNH'], ['banco', 'Inf. Bancária'], ['beneficios', 'Benefícios'], ['pensao', 'Pensão Judicial'],
]

// Valores conhecidos dos combos do ERP; código fora da lista aparece como está
const MEIO_PAGTO: Record<string, string> = { '1': 'Depósito' }
const TIPO_CONTA: Record<string, string> = { C: 'Corrente' }

// Campos numéricos com vírgula na tela (casas decimais)
const DECIMAIS: Record<string, number> = { motcomis: 3, motjor: 2, motvlttvl: 2, motvltsvl: 2 }

// Largura do campo pelo tamanho real da coluna no banco (não esticar campo curto)
const largura = (max: number) => Math.max(56, max * 9 + 28)

const fmtNum = (v: any, casas: number) =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const semZero = (v: any) => (v === null || v === undefined || String(v) === '0' ? '' : String(v))

const LINHA: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }

export default function FuncionarioComplementoModal({ motcod, onClose }: { motcod: string; onClose: () => void }) {
  const [aba, setAba] = useState<Aba>('geral')
  const [f, setF] = useState<Record<string, string> | null>(null)
  const [tab, setTab] = useState<Tabelas>({ classes: [], bancos: [], locais: [], escalas: [], domicilios: [], jornadas: [] })
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/complemento`)
    const d: Record<string, string> = {}
    for (const [k, v] of Object.entries(r.data)) d[k] = v === null || v === undefined ? '' : String(v)
    for (const [k, casas] of Object.entries(DECIMAIS)) d[k] = fmtNum(d[k], casas)
    for (const k of ['mothormes', 'motloc', 'motdom', 'mottabjor', 'motnumpro', 'motnumban', 'motvlttqt', 'motvltsqt', 'motpenba1', 'motpenba2']) d[k] = semZero(d[k])
    setF(d)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar o complemento do funcionário.'))
    api.get('/funcionarios/complemento/tabelas').then(r => setTab(r.data)).catch(() => {})
  }, [motcod])

  if (!f) {
    return (
      <div className="modal-overlay" style={{ zIndex: 1300 }}>
        <div className="modal-box" style={{ maxWidth: 420, padding: '1.5rem', textAlign: 'center', color: erro ? '#dc2626' : '#6b7280' }}>
          {erro || 'Carregando...'}
          {erro && <div style={{ marginTop: '1rem' }}><button className="btn btn-secondary" onClick={onClose}>Fechar</button></div>}
        </div>
      </div>
    )
  }

  const set = (c: string, v: string) => setF(p => ({ ...p!, [c]: v }))
  const juridica = f.motpessoa === 'J'
  const semEscala = f.motesc !== 'S'
  const semBanco = !f.motnumban

  async function salvar() {
    setErro(''); setAviso('')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/complemento`, f)
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Complemento salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o complemento.')
    } finally {
      setSalvando(false)
    }
  }

  // Render helpers (funções, não componentes, pra não perder o foco ao digitar)
  const campo = (w: number, label: string, conteudo: React.ReactNode) => (
    <div className="field-cad" style={{ flex: `0 0 ${w}px` }}>
      <label style={{ whiteSpace: 'nowrap' }}>{label}</label>
      {conteudo}
    </div>
  )
  const txt = (c: string, label: string, max: number, opts: { disabled?: boolean; numerico?: boolean; title?: string } = {}) =>
    campo(Math.max(largura(max), label.length * 7 + 10), label,
      <input value={f[c] ?? ''} maxLength={max} disabled={opts.disabled} title={opts.title}
        inputMode={opts.numerico ? 'decimal' : undefined} style={{ width: '100%', boxSizing: 'border-box' }}
        onChange={e => set(c, e.target.value.toUpperCase())} />)
  const data = (c: string, label: string, opts: { disabled?: boolean } = {}) =>
    campo(140, label,
      <input type="date" value={f[c] ?? ''} disabled={opts.disabled} style={{ width: '100%', boxSizing: 'border-box' }}
        onChange={e => set(c, e.target.value)} />)
  const sel = (c: string, label: string, itens: Item[], w: number, opts: { disabled?: boolean; mostrarCod?: boolean } = {}) =>
    campo(w, label,
      <select value={f[c] ?? ''} disabled={opts.disabled} onChange={e => set(c, e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
        <option value="">—</option>
        {itens.map(i => <option key={String(i.cod)} value={String(i.cod)}>{opts.mostrarCod === false ? i.des : `${i.cod} - ${i.des || '(sem descrição)'}`}</option>)}
        {f[c] && !itens.some(i => String(i.cod) === f[c]) && <option value={f[c]}>{f[c]}</option>}
      </select>)
  const combo = (c: string, label: string, mapa: Record<string, string>) =>
    sel(c, label, Object.entries(mapa).map(([cod, des]) => ({ cod, des })), 130, { mostrarCod: false })
  const simNao = (c: string, label: string) =>
    campo(Math.max(90, label.length * 7 + 10), label,
      <select value={f[c] === 'S' ? 'S' : 'N'} onChange={e => set(c, e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
        <option value="N">Não</option>
        <option value="S">Sim</option>
      </select>)
  const lerSo = (label: string, valor: string, max: number, cor?: string) =>
    campo(Math.max(largura(max), label.length * 7 + 10), label,
      <input value={valor} disabled style={{ width: '100%', boxSizing: 'border-box', ...(cor ? { color: cor, fontWeight: 600 } : {}) }} />)

  const pensao = (n: 1 | 2) => (
    <div style={{ marginTop: n === 2 ? '1rem' : 0 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
        Pensão {n}
      </div>
      <div style={LINHA}>
        {txt(`motpenno${n}`, 'Nome Completo', 40)}
        {txt(`motpencp${n}`, 'CPF', 14, { numerico: true })}
      </div>
      <div style={{ ...LINHA, marginTop: '0.75rem' }}>
        {sel(`motpenba${n}`, 'Banco', tab.bancos, 230)}
        {txt(`motpenag${n}`, 'Agência', 10)}
        {txt(`motpendg${n}`, 'DV', 1)}
        {txt(`motpenco${n}`, 'Número Conta', 10)}
        {txt(`motpendv${n}`, 'DV', 1)}
        {combo(`motpentp${n}`, 'Tipo de Conta', TIPO_CONTA)}
      </div>
    </div>
  )

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 780, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Complemento — {f.motcod} · {f.motnome}</h2>
            <div style={{ fontSize: '0.75rem', color: 'rgba(219,234,254,0.9)', marginTop: 2 }}>
              Admissão: {fmtData(f.motdadmis) || '—'}
            </div>
          </div>
          {/* Código da tela do ERP fica ao lado do X, sem sobrepor o nome */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCA1</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-abas">
          {ABAS.map(([k, label]) => (
            <button key={k} className={`modal-aba${aba === k ? ' modal-aba--ativa' : ''}`} onClick={() => setAba(k)}>{label}</button>
          ))}
        </div>

        <div className="modal-corpo">
          {aba === 'geral' && (
            <div style={LINHA}>
              {txt('motrnt', 'Rntrc', 20)}
              {sel('motlocemb', 'L. de Embarque', tab.locais, 190)}
              {txt('motcomis', 'Prêmios (%)', 7, { numerico: true, title: 'Acerto de Viagens' })}
              {txt('motjor', 'Jornada Diária', 5, { numerico: true })}
              {txt('mothormes', 'Horas Mensais', 4, { numerico: true })}
              {combo('motmei', 'Meio Pagamento TAC', MEIO_PAGTO)}
              {txt('mottac', 'Cartão TAC', 16)}
              {txt('motserie', 'Série/Passagens', 3)}
              {simNao('motesc', 'Tem escala')}
              {sel('motloc', 'Local p/ Escala', tab.escalas, 190, { disabled: semEscala })}
              {sel('motdom', 'Domicílio', tab.domicilios, 170, { disabled: semEscala })}
              {simNao('motbol', 'M.Obra S.Mecânicos')}
              {sel('mottabjor', 'Tabela jornada', tab.jornadas, 190)}
              {txt('motdepe', 'Dependentes', 3, { numerico: true })}
              {data('motdatest', 'Experiência até')}
            </div>
          )}

          {aba === 'cnh' && (
            <div style={LINHA}>
              {txt('motcnh', 'Número Registro', 12, { disabled: juridica, title: juridica ? 'Pessoa jurídica não tem CNH' : undefined })}
              {txt('motesh', 'Estado', 2)}
              {data('motemicnh', 'Dt. Emissão')}
              {data('motpricnh', 'Dt. Pri. Habilitação')}
              {data('motvalid', 'Dt. Validade', { disabled: juridica })}
              {sel('motclasse', 'Categ. Habilitação', tab.classes, 180, { disabled: juridica })}
              {txt('motnumpro', 'Nº Formulário', 11, { numerico: true })}
            </div>
          )}

          {aba === 'banco' && (
            <div style={LINHA}>
              {sel('motnumban', 'Banco', tab.bancos, 230)}
              {txt('motagencia', 'Agência', 10, { disabled: semBanco, title: semBanco ? 'Informe o banco primeiro' : undefined })}
              {txt('motagedig', 'DV', 1, { disabled: semBanco })}
              {txt('motconta', 'Número Conta', 10, { disabled: !f.motagencia, title: !f.motagencia ? 'Informe a agência primeiro' : undefined })}
              {txt('motcontadv', 'DV', 1, { disabled: !f.motagencia })}
              {combo('mottipcon', 'Tipo Conta', TIPO_CONTA)}
            </div>
          )}

          {aba === 'beneficios' && (
            <>
              <div style={LINHA}>
                {txt('motvlttip', 'Tipo', 12)}
                {txt('motvlttqt', 'Quantidade', 6, { numerico: true })}
                {txt('motvlttvl', 'Valor', 10, { numerico: true })}
              </div>
              <div style={{ ...LINHA, marginTop: '0.75rem' }}>
                {txt('motvltsti', 'Subtipo', 12)}
                {txt('motvltsqt', 'Quantidade', 6, { numerico: true })}
                {txt('motvltsvl', 'Valor', 10, { numerico: true })}
              </div>
            </>
          )}

          {aba === 'pensao' && (
            <>
              {pensao(1)}
              {pensao(2)}
            </>
          )}

          {/* Rodapé do ERP: salário e baixa são alterados em outras telas (Salário, Admissão/Demissão) */}
          <div style={{ ...LINHA, marginTop: '1.1rem', paddingTop: '0.9rem', borderTop: '1px solid #e5e7eb' }}>
            {lerSo('Salário Base', fmtNum(f.motsal, 2), 10, '#1a6ea8')}
            {lerSo('Valor Outros', fmtNum(f.motsalout, 2), 10)}
            {lerSo('Encargos Sociais (%)', fmtNum(f.motencsoc, 2), 6)}
          </div>
          <div style={{ ...LINHA, marginTop: '0.75rem' }}>
            {lerSo('Dt. Baixa', fmtData(f.motbdata), 10, '#dc2626')}
            {lerSo('Obs. Baixa', f.motbcom, 40)}
            {lerSo('Usu. Baixa', f.motbaixa, 6)}
          </div>

          {erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
